/**
 * ポータル: 連絡・掲示板（Microsoft Viva Engage 風のソーシャルフィード）
 * アバター付きのフィードカードをクリックするとその場で展開し、本文・資料・既読状況が見える。
 * 回覧は展開時に自動で既読が記録される。投稿・編集は管理者のみ。
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Megaphone, Plus, Pin, Search, Paperclip, CheckCircle2, Users as UsersIcon, Pencil, Archive, ChevronUp } from 'lucide-react';
import * as api from '../../services/api';
import PortalLayout, { PV, Avatar } from './PortalLayout';
import { Modal, fieldStyle, labelStyle, fmtDateTime, fileUrl, MarkdownView, EmptyState } from './ui';

interface Post {
  id: string; post_type: string; title: string; body: string; pinned: number;
  target_type: string; target_ids: string[]; deadline: string | null; require_read: number;
  status: string; author_name: string | null; created_at: string;
  my_read: boolean; attachment_count: number; target_count?: number; read_count?: number;
  attachments?: Array<{ id: string; file_name: string; size: number }>;
  read_status?: Array<{ user_id: string; user_name: string; read_at: string | null }>;
}
interface UserOpt { id: string; user_name: string }
interface VesselOpt { id: string; vessel_name: string }

const chipStyle = (active: boolean): React.CSSProperties => ({
  border: 'none', borderRadius: 999, padding: '7px 16px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
  background: active ? PV.accent : '#fff', color: active ? '#fff' : PV.textSub,
  boxShadow: active ? 'none' : PV.cardShadow, fontFamily: 'inherit',
});

export default function PortalBoardPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const user = useMemo(() => { try { return JSON.parse(localStorage.getItem('admin_user') ?? '{}'); } catch { return {}; } }, []);
  const isAdmin = ['system_admin', 'shore_admin'].includes(user?.user_role ?? '');

  const [posts, setPosts] = useState<Post[]>([]);
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState<Record<string, Post>>({});  // id → 詳細
  const [openId, setOpenId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const openRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ data: Post[] }>('/portal/board', q ? { q } : {});
      setPosts(r.data ?? []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [q]);
  useEffect(() => { void load(); }, [load]);

  /** カードを展開（詳細取得＋未読なら既読を記録） */
  const openDetail = useCallback(async (id: string) => {
    setOpenId(id);
    try {
      const r = await api.get<{ data: Post }>(`/portal/board/${id}`);
      setExpanded(m => ({ ...m, [id]: r.data }));
      if (!r.data.my_read) {
        api.post(`/portal/board/${id}/read`, {})
          .then(() => {
            setPosts(ps => ps.map(p => p.id === id ? { ...p, my_read: true } : p));
            setExpanded(m => m[id] ? { ...m, [id]: { ...m[id]!, my_read: true } } : m);
          })
          .catch(() => { /* 圏外時は次回 */ });
      }
    } catch (e: unknown) { alert(e instanceof Error ? e.message : '取得に失敗しました'); }
  }, []);

  // ホームのフィードから ?post= で遷移してきたとき
  useEffect(() => {
    const id = new URLSearchParams(loc.search).get('post');
    if (id) { void openDetail(id); nav('/portal/board', { replace: true }); }
  }, [loc.search, nav, openDetail]);

  useEffect(() => {
    if (openId && openRef.current) openRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [openId]);

  const filtered = posts.filter(p =>
    (tab === 'all' || (tab === 'unread' ? !p.my_read : p.post_type === tab)));

  return (
    <PortalLayout>
      <div style={{ maxWidth: 780, margin: '0 auto' }}>
        {/* 見出し */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '2px 2px 14px' }}>
          <Megaphone size={20} color={PV.accent} />
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: PV.text }}>連絡・掲示板</h1>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', background: '#fff', borderRadius: 8, boxShadow: PV.cardShadow, padding: '0 4px 0 10px' }}>
              <Search size={14} color="#5c6066" />
              <input style={{ border: 'none', outline: 'none', padding: '9px 8px', fontSize: 13, width: 180, fontFamily: 'inherit', background: 'none' }}
                placeholder="件名・本文を検索" value={q}
                onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') void load(); }} />
            </div>
          </div>
        </div>

        {/* 投稿コンポーザー（管理者・Engage風） */}
        {isAdmin && (
          <button onClick={() => setShowCreate(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', background: '#fff', border: 'none', borderRadius: 12, boxShadow: PV.cardShadow, padding: '14px 18px', cursor: 'pointer', marginBottom: 14, textAlign: 'left', fontFamily: 'inherit' }}>
            <Avatar name={user?.user_name} size={36} />
            <span style={{ flex: 1, color: '#5c6066', fontSize: 13.5, background: '#faf9f8', borderRadius: 999, padding: '10px 16px' }}>
              お知らせ・回覧を投稿する…
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: PV.accent, color: '#fff', borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 700 }}>
              <Plus size={14} /> 投稿
            </span>
          </button>
        )}

        {/* フィルタチップ */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          {([['all', 'すべて'], ['announcement', 'お知らせ'], ['circular', '回覧'], ['unread', '未読のみ']] as const).map(([key, lbl]) => (
            <button key={key} style={chipStyle(tab === key)} onClick={() => setTab(key)}>{lbl}</button>
          ))}
        </div>

        {/* フィード */}
        {loading ? (
          <div style={{ textAlign: 'center', color: '#5c6066', padding: 30, fontSize: 13.5 }}>読み込み中...</div>
        ) : filtered.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 12, boxShadow: PV.cardShadow, padding: 16 }}>
            <EmptyState img={tab === 'unread' ? '/portal/all-done.png' : '/portal/empty-board.png'}
              text={tab === 'unread' ? 'すべて確認済みです' : '投稿はまだありません'} />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {filtered.map(p => {
              const isOpen = openId === p.id;
              const d = expanded[p.id];
              return (
                <div key={p.id} ref={isOpen ? openRef : undefined}
                  style={{ background: '#fff', borderRadius: 12, boxShadow: isOpen ? '0 4px 16px rgba(91,95,199,0.18)' : PV.cardShadow, overflow: 'hidden', border: isOpen ? `1.5px solid ${PV.accentSoft}` : 'none' }}>
                  {/* ヘッダー部（常時表示・クリックで開閉） */}
                  <button onClick={() => isOpen ? setOpenId(null) : void openDetail(p.id)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'none', padding: '15px 18px 0', cursor: 'pointer', fontFamily: 'inherit' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar name={p.author_name} size={38} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: PV.text }}>
                          {p.author_name}
                          <span style={{ fontWeight: 400, color: '#5c6066', fontSize: 11.5, marginLeft: 8 }}>{fmtDateTime(p.created_at)}</span>
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 10.5, fontWeight: 700, padding: '1px 9px', borderRadius: 20, background: p.post_type === 'circular' ? '#fff4ce' : PV.accentSoft, color: p.post_type === 'circular' ? '#8a6914' : PV.accentDark }}>
                            {p.post_type === 'circular' ? '回覧' : 'お知らせ'}
                          </span>
                          {p.pinned === 1 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10.5, color: '#d13438', fontWeight: 700 }}><Pin size={10} /> 固定</span>}
                          {p.deadline && <span style={{ fontSize: 11, color: '#8a6914', fontWeight: 600 }}>確認期限 {p.deadline.replace(/-/g, '/')}</span>}
                          {p.target_type !== 'all' && <span style={{ fontSize: 10.5, color: '#5c6066' }}>（宛先限定）</span>}
                        </div>
                      </div>
                      {!p.my_read
                        ? <span style={{ background: '#d13438', color: '#fff', borderRadius: 999, fontSize: 10.5, fontWeight: 700, padding: '2px 9px', flexShrink: 0 }}>未読</span>
                        : isAdmin
                          ? <span style={{ fontSize: 11.5, color: '#5c6066', flexShrink: 0 }}>既読 {p.read_count ?? 0}/{p.target_count ?? 0}</span>
                          : <CheckCircle2 size={15} color="#107c10" style={{ flexShrink: 0 }} />}
                    </div>
                    <div style={{ fontSize: 15.5, fontWeight: 700, color: PV.text, margin: '10px 0 4px' }}>
                      {p.title}
                      {p.attachment_count > 0 && <Paperclip size={13} color={PV.accent} style={{ verticalAlign: -2, marginLeft: 7 }} />}
                    </div>
                    {!isOpen && (
                      <div style={{ fontSize: 13, color: PV.textSub, lineHeight: 1.7, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', paddingBottom: 14 }}>
                        {p.body}
                      </div>
                    )}
                  </button>

                  {/* 展開部 */}
                  {isOpen && (
                    <div style={{ padding: '6px 18px 16px' }}>
                      <div style={{ background: '#faf9f8', borderRadius: 10, padding: '14px 16px', margin: '6px 0 12px' }}>
                        <MarkdownView text={d?.body ?? p.body} />
                      </div>

                      {(d?.attachments?.length ?? 0) > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                          {d!.attachments!.map(a => (
                            <a key={a.id} href={fileUrl(`/portal/board/attachments/${a.id}`)} target="_blank" rel="noreferrer"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: PV.accentDark, background: PV.accentSoft, borderRadius: 8, padding: '8px 13px', textDecoration: 'none' }}>
                              <Paperclip size={13} /> {a.file_name}
                            </a>
                          ))}
                        </div>
                      )}

                      {isAdmin && d && <AttachmentUploader postId={p.id} onDone={() => void openDetail(p.id)} />}

                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: '#107c10', fontWeight: 700 }}>
                          <CheckCircle2 size={14} /> 既読にしました
                        </span>
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                          {isAdmin && d && (
                            <button className="btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }} onClick={() => { setEditTarget(d); setOpenId(null); }}>
                              <Pencil size={12} /> 編集
                            </button>
                          )}
                          {isAdmin && (
                            <button className="btn" style={{ color: '#d13438', display: 'inline-flex', alignItems: 'center', gap: 5 }} onClick={async () => {
                              if (!confirm('この投稿をアーカイブしますか？（一覧から消えます）')) return;
                              try { await api.del(`/portal/board/${p.id}`); setOpenId(null); void load(); }
                              catch (e: unknown) { alert(e instanceof Error ? e.message : '失敗しました'); }
                            }}>
                              <Archive size={12} /> アーカイブ
                            </button>
                          )}
                          <button className="btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }} onClick={() => setOpenId(null)}>
                            <ChevronUp size={12} /> 閉じる
                          </button>
                        </div>
                      </div>

                      {isAdmin && d?.read_status && (
                        <div style={{ marginTop: 12, borderTop: '1px solid #f3f2f1', paddingTop: 10 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: PV.textSub, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                            <UsersIcon size={13} /> 既読状況（{d.read_status.filter(r => r.read_at).length} / {d.read_status.length}）
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {d.read_status.map(r => (
                              <span key={r.user_id} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 600,
                                padding: '3px 10px', borderRadius: 20,
                                background: r.read_at ? '#dff6dd' : '#fde7e9', color: r.read_at ? '#107c10' : '#a4262c',
                              }}>
                                {r.user_name}{r.read_at ? '' : '（未読）'}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {(showCreate || editTarget) && (
        <PostEditor post={editTarget} onClose={() => { setShowCreate(false); setEditTarget(null); }}
          onSaved={() => { setShowCreate(false); setEditTarget(null); setExpanded({}); void load(); }} />
      )}
    </PortalLayout>
  );
}

function AttachmentUploader({ postId, onDone }: { postId: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: PV.accentDark, cursor: 'pointer', fontWeight: 700 }}>
      <Paperclip size={13} /> {busy ? 'アップロード中...' : '資料を追加（10MBまで）'}
      <input type="file" style={{ display: 'none' }} disabled={busy} onChange={async e => {
        const f = e.target.files?.[0];
        if (!f) return;
        setBusy(true);
        try {
          const form = new FormData();
          form.append('file', f);
          await api.postForm(`/portal/board/${postId}/attachments`, form);
          onDone();
        } catch (err: unknown) { alert(err instanceof Error ? err.message : 'アップロードに失敗しました'); }
        finally { setBusy(false); e.target.value = ''; }
      }} />
    </label>
  );
}

function PostEditor({ post, onClose, onSaved }: { post: Post | null; onClose: () => void; onSaved: () => void }) {
  const [postType, setPostType] = useState(post?.post_type ?? 'announcement');
  const [title, setTitle] = useState(post?.title ?? '');
  const [body, setBody] = useState(post?.body ?? '');
  const [targetType, setTargetType] = useState(post?.target_type ?? 'all');
  const [targetIds, setTargetIds] = useState<string[]>(post?.target_ids ?? []);
  const [deadline, setDeadline] = useState(post?.deadline ?? '');
  const [pinned, setPinned] = useState(post?.pinned === 1);
  const [requireRead, setRequireRead] = useState(post ? post.require_read === 1 : false);
  const [users, setUsers] = useState<UserOpt[]>([]);
  const [vessels, setVessels] = useState<VesselOpt[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<{ data: UserOpt[] }>('/portal/chat/directory').then(r => setUsers(r.data ?? [])).catch(() => { /* noop */ });
    api.get<{ data: VesselOpt[] }>('/vessels').then(r => setVessels(r.data ?? [])).catch(() => { /* noop */ });
  }, []);

  const save = async () => {
    if (!title.trim()) { alert('件名を入力してください'); return; }
    setSaving(true);
    try {
      const payload = {
        post_type: postType, title, body, target_type: targetType,
        target_ids: targetType === 'all' ? [] : targetIds,
        deadline: deadline || null, pinned, require_read: requireRead || postType === 'circular',
      };
      let postId = post?.id;
      if (post) await api.put(`/portal/board/${post.id}`, payload);
      else {
        const created = await api.post<{ data: { id: string } }>('/portal/board', payload);
        postId = created.data.id;
      }
      for (const f of files) {
        const form = new FormData();
        form.append('file', f);
        try { await api.postForm(`/portal/board/${postId}/attachments`, form); }
        catch (e: unknown) { alert(`添付「${f.name}」のアップロードに失敗しました: ${e instanceof Error ? e.message : ''}`); }
      }
      onSaved();
    } catch (e: unknown) { alert(e instanceof Error ? e.message : '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  const toggleId = (id: string) => setTargetIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);

  return (
    <Modal width={680} title={post ? '投稿の編集' : '新規投稿'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', gap: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>種類</label>
            <select style={fieldStyle} value={postType} onChange={e => setPostType(e.target.value)}>
              <option value="announcement">お知らせ（掲示板）</option>
              <option value="circular">回覧（既読確認つき）</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>確認期限（回覧向け・任意）</label>
            <input type="date" style={fieldStyle} value={deadline ?? ''} onChange={e => setDeadline(e.target.value)} />
          </div>
        </div>
        <div>
          <label style={labelStyle}>件名 *</label>
          <input style={fieldStyle} value={title} onChange={e => setTitle(e.target.value)} placeholder="例: 年末調整の書類提出について" />
        </div>
        <div>
          <label style={labelStyle}>本文</label>
          <textarea style={{ ...fieldStyle, minHeight: 140, resize: 'vertical', fontFamily: 'inherit' }} value={body} onChange={e => setBody(e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>宛先</label>
          <select style={{ ...fieldStyle, width: 240 }} value={targetType} onChange={e => { setTargetType(e.target.value); setTargetIds([]); }}>
            <option value="all">全社（全員）</option>
            <option value="vessel">船を指定</option>
            <option value="users">個人を指定</option>
          </select>
          {targetType === 'vessel' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {vessels.map(v => (
                <label key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, border: '1px solid #e1dfdd', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', background: targetIds.includes(v.id) ? PV.accentSoft : '#fff' }}>
                  <input type="checkbox" checked={targetIds.includes(v.id)} onChange={() => toggleId(v.id)} /> {v.vessel_name}
                </label>
              ))}
            </div>
          )}
          {targetType === 'users' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, maxHeight: 160, overflowY: 'auto' }}>
              {users.map(u => (
                <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, border: '1px solid #e1dfdd', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', background: targetIds.includes(u.id) ? PV.accentSoft : '#fff' }}>
                  <input type="checkbox" checked={targetIds.includes(u.id)} onChange={() => toggleId(u.id)} /> {u.user_name}
                </label>
              ))}
            </div>
          )}
        </div>
        {!post && (
          <div>
            <label style={labelStyle}>資料の添付（PDF・画像・Excel等、1件10MBまで・複数可）</label>
            <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.xlsx,.docx,.pptx,.csv,.txt,.zip"
              onChange={e => setFiles(Array.from(e.target.files ?? []))} />
            {files.length > 0 && (
              <div style={{ fontSize: 12.5, color: '#107c10', marginTop: 5 }}>
                {files.map(f => `📎 ${f.name}`).join('　')}
              </div>
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: 18 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={pinned} onChange={e => setPinned(e.target.checked)} /> 上部に固定（ピン留め）
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={requireRead || postType === 'circular'} disabled={postType === 'circular'} onChange={e => setRequireRead(e.target.checked)} /> 既読確認を求める
          </label>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn" onClick={onClose}>キャンセル</button>
          <button className="btn-p" disabled={saving} onClick={() => void save()}>{saving ? '保存中...' : post ? '更新する' : '投稿する'}</button>
        </div>
        <p style={{ fontSize: 12, color: '#5c6066', margin: 0 }}>宛先の相手にはプッシュ通知が届きます。{post ? '添付の追加は投稿を開いた画面から行えます。' : ''}</p>
      </div>
    </Modal>
  );
}
